import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { getSizeToken, getFontTokenStyle, getVariantToken, marginBottomSpacing } from '@koaly/mixins';
import classNames from 'classnames';
import React from 'react';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { useFocus, useHover } from 'use-events';
import { ButtonInner } from './ButtonInner.js';

const StyledButton = styled.button.withConfig({
    shouldForwardProp: (prop) => ![
        'prefix', 'spaceAfter', 'size', 'variant', 'appearance', 'iconOnly',
    ].includes(prop),
}) `
  /* reset */
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  text-decoration: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  /* default */
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.15s ease-in-out 0s;
  border-radius: ${getSizeToken('button', 'borderRadius')};
  /* styling */
  cursor: pointer;
  box-sizing: border-box;
  ${({ width }) => width && `width: ${width}`};
  ${({ size }) => getFontTokenStyle('label', size === 'large' ? 'medium' : size, undefined, 'bold')};
  ${({ elevation, theme }) => `box-shadow: ${theme.elevations[elevation]};`};

  ${({ iconOnly, size }) => iconOnly && css `
    min-width: ${getSizeToken('button', 'mobileHeight', size)};
    height: ${getSizeToken('button', 'mobileHeight', size)};

    ${up('s')} {
      height: ${getSizeToken('button', 'height', size)};
      min-width: ${getSizeToken('button', 'height', size)};
    }
  `}

  ${({ iconOnly, size }) => !iconOnly && css `
    height: ${getSizeToken('button', 'mobileHeight', size)};

    ${up('s')} {
      height: ${getSizeToken('button', 'height', size)};
    }
  `}


  ${({ variant, disabled, elevation, theme }) => !disabled && css `
    background-color: ${getVariantToken('button', 'backgroundColor', variant)};
    color: ${getVariantToken('button', 'color', variant)};

    &:hover, &.hover {
      background-color: ${getVariantToken('button', 'backgroundColorHover', variant)};
      color: ${getVariantToken('button', 'colorHover', variant)};
    }

    &:focus, &.focus, &:active, &.active {
      background-color: ${getVariantToken('button', 'backgroundColorFocus', variant)};
      color: ${getVariantToken('button', 'colorFocus', variant)};
      box-shadow: 0 0 0 2px ${getVariantToken('button', 'borderColorFocus', variant)}
       ${elevation && `,${theme.elevations[elevation]}`};
    }
  `};

  ${({ disabled, variant }) => disabled && css `
    background-color: ${getVariantToken('button', 'disabledBackgroundColor', variant)};
    color: ${getVariantToken('button', 'disabledColor', variant)};
  `};

  ${({ spaceAfter }) => spaceAfter && marginBottomSpacing(spaceAfter)};
`;
const _Button = (_a) => {
    var { children, width, size, variant, elevation, href, onClick, className, spaceAfter, active, hover, focus, prefix, iconOnly, suffix, appearance } = _a, rest = __rest(_a, ["children", "width", "size", "variant", "elevation", "href", "onClick", "className", "spaceAfter", "active", "hover", "focus", "prefix", "iconOnly", "suffix", "appearance"]);
    const [isFocused, bindFocus] = useFocus();
    const [isHovered, bindHover] = useHover();
    return (React.createElement(StyledButton, Object.assign({ as: href ? 'a' : 'button', href: href, appearance: appearance, spaceAfter: spaceAfter, onClick: onClick, size: size, className: classNames(className, { active: active, hover: hover, focus: focus }), variant: variant, elevation: elevation, iconOnly: iconOnly, width: width }, rest, bindFocus, bindHover),
        React.createElement(ButtonInner, { hover: isHovered, focus: isFocused, variant: variant, appearance: appearance, size: size, prefix: prefix, suffix: suffix, iconOnly: iconOnly }, children)));
};
_Button.defaultProps = {
    variant: 'primary',
    size: 'medium',
    appearance: 'default',
};
const Button = _Button;

export { Button, StyledButton };
//# sourceMappingURL=Button.js.map
